import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DEFAULT_DATE_FORMAT, formatDate } from '@app/helpers/date.helper';
import { FixSafariCalendarIcon } from '@app/helpers/helpers';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { DateLocalizationService } from '@app/services/date-localization.service';
import { UserSession } from '@app/store/models';
import { ReportLogs } from '@app/store/models/certificate.model';
import { selectSession } from '@app/store/selectors/session.selector';
import { GlobalState } from '@app/store/store';
import { DateSelectRangeValue, DropdownItem } from '@infocert/ng-ui-kit/models';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { chunk } from 'lodash-es';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable } from 'rxjs';

export type ViewFilter = 'ALL' | 'COMPLETED' | 'FAILED';
export type PeriodFilter = 'last_month' | 'last_year' | 'last_week' | 'last_3_months';

@Component({
  selector: 'app-action-report',
  templateUrl: './action-report.component.html',
  styleUrls: ['./action-report.component.scss'],
  standalone: false,
})
export class ActionReportPageComponent implements OnInit, AfterViewInit {
  constructor(
    private certificateService: CertificateService,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<GlobalState>,
    private dateLocalizationService: DateLocalizationService,
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  public getScreenWidth: any;
  public getScreenHeight: any;

  lastSignedDate: string = '';
  viewType: ViewFilter = 'ALL';
  periodType: PeriodFilter = 'last_month'; // default selezionato (ultimo mese)
  pendingViewType: ViewFilter = 'ALL';
  pendingPeriodType: PeriodFilter = 'last_month';
  itemsPerPage: number = 6;
  currentPage: number = 1;
  periodOptions: DropdownItem[] = [];
  resultOptions: DropdownItem[] = [];

  //@ts-ignore
  _reportLogs: ReportLogs[] = [];
  orderOptions: DropdownItem[] = [];
  valueRange: DateSelectRangeValue = {
    dateFrom: null,
    dateTo: null,
  };
  appliedValueRange: DateSelectRangeValue = {
    dateFrom: null,
    dateTo: null,
  };

  options: DropdownItem[] = [
    {
      label: '5',
    },
    {
      label: '10',
    },
    {
      label: '25',
    },
    {
      label: '50',
    },
  ];

  onChangeDateFrom(event: Date | { value?: Date }) {
    const date = (event as any)?.value ?? event;
    this.valueRange.dateFrom = this._clampDateToBounds(date || null);
    // Se l'utente cambia manualmente le date, resetta la selezione periodo su "tutte"
    this._resetPeriodSelection();
  }

  onChangeDateTo(event: Date | { value?: Date }) {
    const date = (event as any)?.value ?? event;
    this.valueRange.dateTo = this._clampDateToBounds(date || null);
    // Se l'utente cambia manualmente le date, resetta la selezione periodo su "tutte"
    this._resetPeriodSelection();
  }

  get startDateValue(): string | null {
    if (!this.appliedValueRange?.dateFrom) {
      return null;
    }
    // Usa il range applicato (solo dopo "Cerca")
    return formatDate(moment(this.appliedValueRange.dateFrom));
  }

  get endDateValue(): string | null {
    if (!this.appliedValueRange?.dateTo) {
      return null;
    }
    // Usa il range applicato (solo dopo "Cerca")
    return formatDate(moment(this.appliedValueRange.dateTo));
  }

  get reportLogs(): ReportLogs[] {
    return (
      this?._reportLogs?.filter(log => {
        const logDate = moment(log.value.executeDate);

        const isInDateRange =
          (!this.startDateValue ||
            logDate.isSameOrAfter(this.startDateValue, 'day')) &&
          (!this.endDateValue ||
            logDate.isSameOrBefore(this.endDateValue, 'day'));

        const matchView =
          this.viewType === 'ALL'
            ? true
            : this.viewType === 'FAILED'
              ? log.value.result === 'FAILED' || log.value.result === 'ERROR'
              : log.value.result === this.viewType;

        return isInDateRange && matchView;
      }) || []
    );
  }

  get paginatedReportLogs(): ReportLogs[] {
    return (
      chunk(this.reportLogs || [], this.itemsPerPage)[this.currentPage - 1] ||
      []
    );
  }

  get totalItems(): number {
    return this?.reportLogs?.length || 0;
  }

  get paginationStart(): number {
    if (this.totalItems === 0) {
      return 0;
    }
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get paginationEnd(): number {
    if (this.totalItems === 0) {
      return 0;
    }
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }

  filterRegion1: string = '';
  filterRegion2: string = '';
  private _activeFilterRegion = 1;
  loading: boolean = false;
  checkWeeklySigns: boolean = false;
  checkTotalSigns: boolean = true;
  userSession$: Observable<UserSession>;
  reversed: boolean = false;
  showMobileFilter: boolean = false;
  /** Limite inferiore selezionabile nel date-range (oggi - 1 mese). */
  dateMinBound: Date | null = null;
  /** Limite superiore selezionabile nel date-range (oggi). */
  dateMaxBound: Date | null = null;

  @HostListener('window:resize')
  onWindowResize() {
    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;
  }

  get isMobile(): boolean {
    return this.getScreenWidth <= 578;
  }

  get showFilter(): boolean {
    if (this.isMobile) {
      return this.showMobileFilter;
    } else {
      return true;
    }
  }

  ngOnInit(): void {
    this.userSession$ = this.store.select(selectSession);

    // Imposta il limite minimo selezionabile nel calendario: oggi - 1 mese
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);
    this.dateMinBound = oneMonthAgo;
    this.dateMaxBound = today;

    this.route.queryParams.subscribe((params: Params) => {
      let shouldApplyRange = false;
      let period: PeriodFilter = 'last_month';

      if (params['date'] === '1-week') {
        period = 'last_week';
        shouldApplyRange = true;
        this._applyPeriodRange(period);
        this.periodType = period;
        this.pendingPeriodType = period; // ✅ sincronizza
        this.checkWeeklySigns = true;
        this.checkTotalSigns = false;
      } else if (params['date'] === '3-months') {
        period = 'last_3_months';
        shouldApplyRange = true;
        this._applyPeriodRange(period);
        this.periodType = period;
        this.pendingPeriodType = period; // ✅ sincronizza
        this.checkTotalSigns = false;
        this.checkWeeklySigns = false;
      } else {
        period = 'last_month';
        shouldApplyRange = true;
        this.periodType = period;
        this.pendingPeriodType = period; // ✅ sincronizza
        this.checkTotalSigns = true;
        this.checkWeeklySigns = false;
      }
      // ⏱ Timeout di 50-100ms per dare tempo ad Angular di settare tutto
      setTimeout(() => {
        this._initFilterOptions();
        // Placeholder separati per i due input del date-range
        this._updateDateRangePlaceholders();
        if (shouldApplyRange) {
          // Applica sempre il range sul selettore date al primo ingresso
          this._applyPeriodRange(period);
          this.getActionReport(this.startDate.value, this.endDate.value); // Chiama API
          this.appliedValueRange = {
            dateFrom: this.valueRange?.dateFrom || null,
            dateTo: this.valueRange?.dateTo || null,
          };
        } else {
          const { startDate, endDate } = this._getPeriodDates(period);
          this.getActionReport(startDate, endDate);
        }
      }, 100);
    });

    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;
    //setTimeout(() => this.onChangeItemsPerPage(this.options[0]), 500);

    FixSafariCalendarIcon();
    this.translateService.onLangChange.subscribe(() => {
      this._initFilterOptions();
      this._updateDateRangePlaceholders();
    });
    this.initOrderOptions();
  }

  ngAfterViewInit(): void {
    // Attendi il render del date-select prima di forzare i placeholder
    setTimeout(() => this._updateDateRangePlaceholders());
  }

  getActionReport(startDate: string, endDate: string) {
    this.loading = true;
    this.lastSignedDate = '';
    this.certificateService
      .getActionReport(startDate, endDate)
      .then(response => {
        const logs = response || [];
        this._reportLogs = logs.sort((a, b) => {
          const aTime = new Date(a?.value?.executeDate || 0).getTime();
          const bTime = new Date(b?.value?.executeDate || 0).getTime();
          return this.reversed ? aTime - bTime : bTime - aTime;
        });
        this.loading = false;
        const message = this.translateService.instant(
          'ACTION_REPORT_PAGE.FILTER_RESULTS_ANNOUNCEMENT',
          { count: this._reportLogs.length }
        );
        if (this._activeFilterRegion === 1) {
          this.filterRegion1 = message;
          this.filterRegion2 = '';
          this._activeFilterRegion = 2;
        } else {
          this.filterRegion1 = '';
          this.filterRegion2 = message;
          this._activeFilterRegion = 1;
        }
      })
      .catch(error => {
        console.log(error);
        this.loading = false;
      });
  }

  startDate = new UntypedFormControl('', [Validators.required]);

  endDate = new UntypedFormControl('', [Validators.required]);

  form = new UntypedFormGroup({
    startDate: this.startDate,
    endDate: this.endDate,
  });

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  get formErrors() {
    const errors = this.form.errors;
    return errors;
  }

  get minDate(): string {
    return moment(this.startDate.value).format(DEFAULT_DATE_FORMAT);
  }

  onSubmit() {
    this.loading = true;

    // Applica periodo
    this.periodType = this.pendingPeriodType;
    this.viewType = this.pendingViewType;
    // Se l'utente ha compilato manualmente il range, usa quello
    const manualRange = this._readDateRangeInputs();
    const effectiveRange =
      manualRange.dateFrom || manualRange.dateTo ? manualRange : this.valueRange;
    if (effectiveRange?.dateFrom || effectiveRange?.dateTo) {
      this.valueRange = effectiveRange;
      this.router.navigate([], { queryParams: {} });
    } else {
      this._applyPeriodRange(this.periodType);
      switch (this.periodType) {
        case 'last_week':
          this.router.navigate([], { queryParams: { date: '1-week' } });
          break;
        case 'last_3_months':
          this.router.navigate([], { queryParams: { date: '3-months' } });
          break;
        case 'last_year':
          this.router.navigate([], { queryParams: {} });
          break;
      }
    }

    const start = this.valueRange?.dateFrom
      ? moment(this.valueRange.dateFrom).format('YYYY-MM-DD')
      : '';
    const end = this.valueRange?.dateTo
      ? moment(this.valueRange.dateTo).format('YYYY-MM-DD')
      : '';

    this.currentPage = 1;

    //Chiama la API
    this.getActionReport(start, end);
    // Salva il range applicato per il filtro client-side
    this.appliedValueRange = {
      dateFrom: this.valueRange?.dateFrom || null,
      dateTo: this.valueRange?.dateTo || null,
    };

    this.loading = false;
  }

  getFormattedSubject(subject: string) {
    const props = subject.split(',');
    let _subject = {};

    for (let i = 0; i < props.length; i++) {
      let key = props[i].split('=')[0];
      let value = props[i].split('=')[1];
      _subject[key] = value;
    }

    return _subject as any;
  }

  getLogDate(date: string) {
    if (this.lastSignedDate === '') {
      this.lastSignedDate = this.dateLocalizationService.formatDisplayDate(
        moment(date).utc().local()
      );
    }

    return this.dateLocalizationService.formatDisplayDate(
      moment(date).utc().local()
    );
  }

  getLogTime(date: string) {
    if (this.lastSignedDate?.length === 10) {
      this.lastSignedDate += ' ' + moment(date).utc().local().format('HH.mm');
    }

    return moment(date).utc().local().format('HH.mm');
  }

  setDate(date: string, e?: any) {
    if (e.target.checked) {
      let startDate = formatDate(moment());
      let endDate = formatDate(moment());

      switch (date) {
        case 'last_week':
          this.router.navigate([], { queryParams: { date: '1-week' } });
          break;
        case 'last_3_months':
          this.router.navigate([], { queryParams: { date: '3-months' } });
          break;
        case 'last_year':
          this.router.navigate([], { queryParams: {} });
          break;
      }
    }
  }

  resetDate() {
    let startDate = formatDate(moment());
    let endDate = formatDate(moment());

    this.startDate.setValue(startDate);
    this.endDate.setValue(endDate);

    this.getActionReport(startDate, endDate);
  }

  viewOnly(result: ViewFilter, e?: any) {
    if (e.target.checked) {
      this.viewType = result;
      this.currentPage = 1;
    }
  }

  orderLogs() {
    this._reportLogs.reverse();
    if (!this.reversed) {
      this.reversed = true;
    } else {
      this.reversed = false;
    }
  }

  private _initFilterOptions(): void {
    const keys = [
      'ACTION_REPORT_PAGE.VIEW_ALL_SIGNS',
      'ACTION_REPORT_PAGE.LAST_7_DAYS',
      'ACTION_REPORT_PAGE.LAST_3_MONTHS',
      'ACTION_REPORT_PAGE.VIEW_ALL_RESULTS',
      'ACTION_REPORT_PAGE.COMPLETED',
      'ACTION_REPORT_PAGE.FAILED',
    ];

    this.translateService.get(keys).subscribe(translations => {
      this.periodOptions = [
        {
          label: translations['ACTION_REPORT_PAGE.VIEW_ALL_SIGNS'],
          value: 'last_year',
          selected: this.pendingPeriodType === 'last_year',
        },
        {
          label: translations['ACTION_REPORT_PAGE.LAST_7_DAYS'],
          value: 'last_week',
          selected: this.pendingPeriodType === 'last_week',
        },
        {
          label: translations['ACTION_REPORT_PAGE.LAST_3_MONTHS'],
          value: 'last_3_months',
          selected: this.pendingPeriodType === 'last_3_months',
        },
      ];

      this.resultOptions = [
        {
          label: translations['ACTION_REPORT_PAGE.VIEW_ALL_RESULTS'],
          value: 'ALL',
          selected: this.pendingViewType === 'ALL',
        },
        {
          label: translations['ACTION_REPORT_PAGE.COMPLETED'],
          value: 'COMPLETED',
          selected: this.pendingViewType === 'COMPLETED',
        },
        {
          label: translations['ACTION_REPORT_PAGE.FAILED'],
          value: 'FAILED',
          selected: this.pendingViewType === 'FAILED',
        },
      ];
    });
  }

  copyToClipboard(note: string) {
    try {
      navigator.clipboard.writeText(note);
    } catch (e) {
      console.warn('navigator.clipboard is not supported');
      const selBox = document.createElement('textarea');
      selBox.style.position = 'fixed';
      selBox.style.left = '0';
      selBox.style.top = '0';
      selBox.style.opacity = '0';
      selBox.value = note;
      document.body.appendChild(selBox);
      selBox.focus();
      selBox.select();
      document.execCommand('copy');
      document.body.removeChild(selBox);
    } finally {
      this.toasterService.success(
        '',
        this.translateService.instant('ACTION_REPORT_PAGE.COPY_TO_CLIPBOARD'),
        {
          positionClass: 'toast-top-right',
        }
      );
    }
  }

  public onPageChanged(data: any): void {
    const nextPage =
      typeof data === 'number'
        ? data
        : data?.currentPage ??
          data?.page ??
          data?.detail?.currentPage ??
          data?.detail?.page;

    if (typeof nextPage === 'number' && Number.isFinite(nextPage)) {
      this.currentPage = nextPage;
    }
  }

  onChangeItemsPerPage(item: DropdownItem) {
    console.log(
      '🚀 ~ ActionReportPageComponent ~ onChangeItemsPerPage ~ item',
      item
    );
    this.options = this.options.map(o => {
      o.selected = o.label === item.label;
      return o;
    });
    this.itemsPerPage = parseInt(this.options.find(o => o.selected).label);
  }

  onPeriodChange(item: DropdownItem) {
    const value = (item?.value ?? item?.label) as PeriodFilter;
    this.pendingPeriodType = value;
    this.periodOptions = this.periodOptions.map(option => ({
      ...option,
      selected: option.value === value,
    }));
    // Aggiorna subito il calendario con il periodo selezionato
    this._setPendingPeriodRange(value);
  }

  onResultChange(item: DropdownItem) {
    const value = (item?.value ?? item?.label) as ViewFilter;
    this.pendingViewType = value;
    this.resultOptions = this.resultOptions.map(option => ({
      ...option,
      selected: option.value === value,
    }));
  }

  private _applyPeriodRange(period: PeriodFilter): void {
    const todayDate = moment();
    let from = todayDate.clone().subtract(1, 'month');

    if (period === 'last_week') {
      from = todayDate.clone().subtract(1, 'week');
    } else if (period === 'last_month') {
      from = todayDate.clone().subtract(1, 'month');
    } else if (period === 'last_3_months') {
      from = todayDate.clone().subtract(3, 'months');
    }

    this.valueRange = {
      dateFrom: from.toDate(),
      dateTo: todayDate.toDate(),
    };
    // Allinea anche il range applicato
    this.appliedValueRange = {
      dateFrom: from.toDate(),
      dateTo: todayDate.toDate(),
    };
    this.startDate.setValue(formatDate(from));
    this.endDate.setValue(formatDate(todayDate));
  }

  private _setPendingPeriodRange(period: PeriodFilter): void {
    const { startDate, endDate } = this._getPeriodDates(period);
    const startMoment = moment(startDate, 'YYYY-MM-DD', true);
    const endMoment = moment(endDate, 'YYYY-MM-DD', true);

    this.valueRange = {
      dateFrom: startMoment.isValid() ? startMoment.toDate() : null,
      dateTo: endMoment.isValid() ? endMoment.toDate() : null,
    };
  }

  private _syncFilterSelections(): void {
    // Mantiene selezionati i valori pending nei dropdown
    this.periodOptions = this.periodOptions.map(option => ({
      ...option,
      selected: option.value === this.pendingPeriodType,
    }));
    this.resultOptions = this.resultOptions.map(option => ({
      ...option,
      selected: option.value === this.pendingViewType,
    }));
  }

  private _resetPeriodSelection(): void {
    // Seleziona "tutte le firme" quando si modifica il range manualmente
    this.pendingPeriodType = 'last_month';
    this._syncFilterSelections();
  }

  private _readDateRangeInputs(): DateSelectRangeValue {
    // Legge il valore testuale dei due input e lo converte in Date
    const startInput = this.elementRef.nativeElement.querySelector(
      '#date-range-idstart'
    ) as HTMLInputElement | null;
    const endInput = this.elementRef.nativeElement.querySelector(
      '#date-range-idend'
    ) as HTMLInputElement | null;
    const parseDate = (value: string | null | undefined): Date | null => {
      const trimmed = (value || '').trim();
      if (!trimmed) {
        return null;
      }
      // Accetta sia formato italiano sia ISO
      const parsed = moment(trimmed, ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD']);
      return parsed.isValid() ? parsed.toDate() : null;
    };

    return {
      dateFrom: this._clampDateToBounds(parseDate(startInput?.value)),
      dateTo: this._clampDateToBounds(parseDate(endInput?.value)),
    };
  }

  private _updateDateRangePlaceholders(): void {
    // Forza placeholder distinti per "Da" e "A"
    const inputs = this.elementRef.nativeElement.querySelectorAll(
      'ng-ui-kit-date-select input.ic1-date-select'
    );

    if (!inputs || inputs.length < 2) {
      return;
    }

    const fromKey = 'ACTION_REPORT_PAGE.PLACEHOLDER_FROM';
    const toKey = 'ACTION_REPORT_PAGE.PLACEHOLDER_TO';

    this.translateService.get([fromKey, toKey]).subscribe(translations => {
      const placeholderFrom = translations[fromKey] || '';
      const placeholderTo = translations[toKey] || '';
      this.renderer.setAttribute(inputs[0], 'placeholder', placeholderFrom);
      this.renderer.setAttribute(inputs[1], 'placeholder', placeholderTo);
    });
  }

  private _getPeriodDates(period: PeriodFilter): {
    startDate: string;
    endDate: string;
  } {
    const todayDate = moment();
    let from = todayDate.clone().subtract(1, 'month');

    if (period === 'last_week') {
      from = todayDate.clone().subtract(1, 'week');
    } else if (period === 'last_month') {
      from = todayDate.clone().subtract(1, 'month');
    } else if (period === 'last_3_months') {
      from = todayDate.clone().subtract(3, 'months');
    }

    return {
      startDate: formatDate(from),
      endDate: formatDate(todayDate),
    };
  }

  onResetTreeFilters(): void {
    // Reimposta viewType e date (mostra tutto)
    this.periodType = 'last_month';
    this.viewType = 'ALL';
    this.pendingPeriodType = 'last_month';
    this.pendingViewType = 'ALL';
    this._applyPeriodRange('last_month');

    // Aggiorna URL (senza parametri) e richiama i dati
    this.router.navigate([], { queryParams: {} });
    const { startDate, endDate } = this._getPeriodDates('last_month');
    this.getActionReport(startDate, endDate);
    // Ripristina anche i placeholder tradotti
    this._updateDateRangePlaceholders();

    this.periodOptions = this.periodOptions.map(option => ({
      ...option,
      selected: option.value === 'last_month',
    }));
    this.resultOptions = this.resultOptions.map(option => ({
      ...option,
      selected: option.value === 'ALL',
    }));
  }
  isDropdownOpen: boolean = false;

  initOrderOptions(): void {
    const updateTranslations = () => {
      const keys = [
        'ACTION_REPORT_PAGE.MOST_RECENT_SIGN',
        'ACTION_REPORT_PAGE.MOST_OLD_SIGN',
      ];

      this.translateService.get(keys).subscribe(translations => {
        this.orderOptions = [
          {
            label: translations['ACTION_REPORT_PAGE.MOST_RECENT_SIGN'],
            value: 'DESC',
            selected: !this.reversed,
          },
          {
            label: translations['ACTION_REPORT_PAGE.MOST_OLD_SIGN'],
            value: 'ASC',
            selected: this.reversed,
          },
        ];
      });
    };

    // Primo caricamento
    updateTranslations();

    // Ascolta i cambi di lingua
    this.translateService.onLangChange.subscribe(() => {
      updateTranslations();
    });
  }

  toggleDropdown(isOpen?: boolean): void {
    this.isDropdownOpen =
      typeof isOpen === 'boolean' ? isOpen : !this.isDropdownOpen;
  }

  onSelectOrder(value: 'ASC' | 'DESC'): void {
    this.reversed = value === 'ASC';
    this._reportLogs.reverse();

    // Aggiorna i selected correttamente
    this.orderOptions = this.orderOptions.map(option => ({
      ...option,
      selected: option.value === value,
    }));
  }

  private _clampDateToBounds(value: Date | null): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (this.dateMinBound && date < this.dateMinBound) {
      return new Date(this.dateMinBound);
    }
    if (this.dateMaxBound && date > this.dateMaxBound) {
      return new Date(this.dateMaxBound);
    }
    return date;
  }

}
